import { useCallback, useEffect, useRef, useState } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { useAuth } from '../contexts/AuthContext';
import type { DealStatus } from '../constants/dealTracker';

type UseDealTrackingArgs = {
  startupId?: string;
  investorId?: string;
};

type DisclosureConsent = 'unknown' | 'granted' | 'denied' | null;

type UseDealTrackingResult = {
  dealStatus: DealStatus;
  disclosureConsent: DisclosureConsent;
  hasStartupFeedback: boolean;
  isLoading: boolean;
  isUpdating: boolean;
  error: string | null;
  refreshStatus: () => Promise<void>;
  updateDealStatus: (newStatus: DealStatus) => Promise<void>;
  /** Clear the tracker locally after unfollow so Follow/Contacted do not stay green. */
  resetToUnfollowed: () => void;
  /** Revert a failed unfollow, or mark the pair active again before a re-follow refresh. */
  restoreLocalStatus: (status: DealStatus) => void;
};

const DEFAULT_STATUS: DealStatus = 'not-contacted';

/** True when this pair already has an inbox thread, so contact has started. */
async function threadHasMessages(
  functions: ReturnType<typeof getFunctions>,
  investorId: string,
  startupId: string
): Promise<boolean> {
  const getMessages = httpsCallable<
    { conversationId: string; limit?: number },
    { success: boolean; messages?: unknown[]; error?: string }
  >(functions, 'getConversationMessages');
  const result = await getMessages({
    conversationId: `${investorId}_${startupId}`,
    limit: 1,
  });
  const data = result.data;
  if (!data.success) return false;
  return Array.isArray(data.messages) && data.messages.length > 0;
}

export const useDealTracking = ({
  startupId,
  investorId,
}: UseDealTrackingArgs): UseDealTrackingResult => {
  const { user } = useAuth();
  const functions = getFunctions();

  const [dealStatus, setDealStatus] = useState<DealStatus>(DEFAULT_STATUS);
  const [disclosureConsent, setDisclosureConsent] = useState<DisclosureConsent>(null);
  const [hasStartupFeedback, setHasStartupFeedback] = useState(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isUpdating, setIsUpdating] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const fetchGenRef = useRef(0);
  const unfollowedRef = useRef(false);

  const isInvestor = user?.userType === 'investor';
  const isStartup = user?.userType === 'startup';

  const hasRequiredIds = (() => {
    if (!user) return false;
    if (isInvestor) return !!startupId;
    if (isStartup) return !!investorId;
    return false;
  })();

  const refreshStatus = useCallback(async () => {
    if (!user || !hasRequiredIds) return;

    const requestStartupId = startupId;
    const requestInvestorId = investorId;
    const fetchGen = ++fetchGenRef.current;
    setIsLoading(true);
    setError(null);

    try {
      const getStatusFn = httpsCallable(functions, 'getDealStatus');

      const payload: Record<string, unknown> = {};
      if (isInvestor && requestStartupId) {
        payload.startupId = requestStartupId;
      } else if (isStartup && requestInvestorId) {
        payload.investorId = requestInvestorId;
      }

      const result = await getStatusFn(payload);
      // Ignore late responses after unfollow or after the tracked pair changed.
      if (fetchGen !== fetchGenRef.current) return;
      if (requestStartupId !== startupId || requestInvestorId !== investorId) return;

      const data = result.data as {
        success: boolean;
        status?: DealStatus;
        disclosureConsent?: DisclosureConsent;
        hasStartupFeedback?: boolean;
        favoriteActive?: boolean;
        error?: string;
      };

      if (data.success && data.status) {
        let status = data.status;
        if (
          data.favoriteActive === false ||
          (unfollowedRef.current && data.favoriteActive !== true)
        ) {
          status = DEFAULT_STATUS;
        } else if (data.favoriteActive === true) {
          unfollowedRef.current = false;
        }
        // Inbox messages mean contact already happened, even if the tracker was never advanced.
        if (
          isStartup &&
          requestInvestorId &&
          user.id &&
          (status === 'favorited' || status === 'not-contacted')
        ) {
          try {
            const hasThread = await threadHasMessages(functions, requestInvestorId, user.id);
            if (fetchGen !== fetchGenRef.current) return;
            if (requestStartupId !== startupId || requestInvestorId !== investorId) return;
            if (hasThread) {
              const updateFn = httpsCallable<
                { investorId: string; newStatus: DealStatus },
                { success: boolean; currentStatus?: DealStatus }
              >(functions, 'updateDealStatus');
              const updated = await updateFn({
                investorId: requestInvestorId,
                newStatus: 'contacted',
              });
              if (fetchGen !== fetchGenRef.current) return;
              if (requestStartupId !== startupId || requestInvestorId !== investorId) return;
              if (updated.data.success) {
                status = updated.data.currentStatus || 'contacted';
              }
            }
          } catch {
            // Leave the stored step if the thread check fails.
          }
        }
        setDealStatus(status);
        setDisclosureConsent(data.disclosureConsent ?? null);
        setHasStartupFeedback(data.hasStartupFeedback === true);
      } else if (!data.success) {
        setError(data.error ?? 'Failed to fetch deal status');
      }
    } catch (err: any) {
      if (fetchGen !== fetchGenRef.current) return;
      if (requestStartupId !== startupId || requestInvestorId !== investorId) return;
      setError(err?.message || 'Failed to fetch deal status');
    } finally {
      if (fetchGen === fetchGenRef.current) {
        if (requestStartupId === startupId && requestInvestorId === investorId) {
          setIsLoading(false);
        }
      }
    }
  }, [functions, user, hasRequiredIds, isInvestor, isStartup, startupId, investorId]);

  const resetToUnfollowed = useCallback(() => {
    fetchGenRef.current += 1;
    unfollowedRef.current = true;
    setDealStatus(DEFAULT_STATUS);
    setDisclosureConsent(null);
    setIsLoading(false);
  }, []);

  const restoreLocalStatus = useCallback((status: DealStatus) => {
    unfollowedRef.current = false;
    setDealStatus(status);
  }, []);

  const updateDealStatus = useCallback(
    async (newStatus: DealStatus) => {
      if (!user || !hasRequiredIds) return;

      // Optimistic update: reflect the new status immediately so the tracker
      // feels instant, then reconcile (or revert) once the server responds.
      unfollowedRef.current = false;
      let previousStatus: DealStatus = DEFAULT_STATUS;
      setDealStatus(prev => {
        previousStatus = prev;
        return newStatus;
      });
      setIsUpdating(true);
      setError(null);

      try {
        const updateFn = httpsCallable(functions, 'updateDealStatus');

        const payload: Record<string, unknown> = { newStatus };
        if (isInvestor && startupId) {
          payload.startupId = startupId;
        } else if (isStartup && investorId) {
          payload.investorId = investorId;
        }

        const result = await updateFn(payload);
        const data = result.data as {
          success: boolean;
          currentStatus?: DealStatus;
          error?: string;
        };

        if (data.success && data.currentStatus) {
          setDealStatus(data.currentStatus);
        } else if (!data.success) {
          // Revert the optimistic change on a failed write.
          setDealStatus(previousStatus);
          setError(data.error ?? 'Failed to update deal status');
        }
      } catch (err: any) {
        setDealStatus(previousStatus);
        setError(err?.message || 'Failed to update deal status');
      } finally {
        setIsUpdating(false);
      }
    },
    [functions, user, hasRequiredIds, isInvestor, isStartup, startupId, investorId]
  );

  useEffect(() => {
    fetchGenRef.current += 1;
    unfollowedRef.current = false;
    setDealStatus(DEFAULT_STATUS);
    setDisclosureConsent(null);
    setHasStartupFeedback(false);
    setError(null);
    if (!user || !hasRequiredIds) return;
    void refreshStatus();
  }, [user, hasRequiredIds, startupId, investorId, refreshStatus]);

  return {
    dealStatus,
    disclosureConsent,
    hasStartupFeedback,
    isLoading,
    isUpdating,
    error,
    refreshStatus,
    updateDealStatus,
    resetToUnfollowed,
    restoreLocalStatus,
  };
};
